import { object, str, unknownValue, type FieldParser } from 'validation';

// Shared value types for the triggers endpoints: the three enum domains, and the
// `parameters` element shape.
//
// The enums mirror `nap-backend` modules/utils/validation.ts
// (`TRIGGER_TYPES` / `TRIGGER_ACTIONS` / `TRIGGER_RESTART_FIRE_MODES`), which every
// trigger route validates against. `restart_fire_mode` additionally carries a DB
// CHECK constraint (migration 0275), so it is a closed set at rest too.
//
// `TriggerParameter` is shared by true IDENTITY across every trigger endpoint: it
// is the content of ONE JSONB column, `triggers_node_status_change.parameters`,
// read back verbatim by each handler and round-tripped unchanged on write. The
// flat/nested record ENVELOPES around it are not shared — each route handler
// assembles its own (see ../../README.md).

export const TRIGGER_TYPES = ['node_status_change'] as const;
export type TriggerType = (typeof TRIGGER_TYPES)[number];

export const TRIGGER_ACTIONS = ['raise_event', 'submit_child'] as const;
export type TriggerAction = (typeof TRIGGER_ACTIONS)[number];

export const TRIGGER_RESTART_FIRE_MODES = ['all', 'first', 'last', 'first_and_last'] as const;
export type TriggerRestartFireMode = (typeof TRIGGER_RESTART_FIRE_MODES)[number];

/**
 * The backend's default when `restart_fire_mode` is absent from a write body
 * (`TRIGGER_RESTART_FIRE_MODE_DEFAULT`), and the DB column default.
 *
 * NOTE: this is the default for what we SEND, not a read fallback — on read the
 * field is `nullable`, and `nullable` overrides an inner parser's default with
 * `null`, so a drifted value surfaces as null rather than as this mode. Seed a
 * create form with it; don't use it to paper over a read.
 */
export const TRIGGER_RESTART_FIRE_MODE_DEFAULT: TriggerRestartFireMode = 'last';

/**
 * One parameter override for the `submit_child` action. `value` is usually an
 * expression string: it may embed template variables the trigger engine substitutes
 * at fire time (`{{trigger_node_instance_id}}`, `{{trigger_node_definition_id}}`,
 * `{{trigger_node_definition_name}}`, `{{trigger_status_from}}`,
 * `{{trigger_status_to}}`) — which is why a value editor for these has to accept
 * free text whatever the target parameter's declared type.
 *
 * But it is ANY JSON VALUE on the wire, not a string, and must be declared so: a
 * value this type cannot hold is one the edit dialog refuses to open on. The routes
 * store the array into JSONB without checking element types, the engine resolves
 * templates only in strings and passes anything else through verbatim, and the
 * child submit coerces per the target parameter's type — `parseFloat` for a
 * numeric one, a boolean or the string `'true'` for a boolean one, and for a JSON
 * one either a string it parses or an object it takes as-is. So a number or an
 * object here is legitimate, typically from a trigger created through the API, and
 * a string is accepted whatever the target type — which is what the form sends for
 * a value the user edits.
 */
export interface TriggerParameter {
  readonly name: string;
  readonly value: unknown;
}

export const triggerParameterSchema: FieldParser<TriggerParameter> = object<TriggerParameter>({
  name: str(),
  value: unknownValue(),
});
